import { useState, useEffect } from 'react';
import { API_BASE_URL } from '../../api';

const PRODUCTS_PER_PAGE = 25;

const emptyColorRow = () => ({ name: '', code: '#000000' });

const emptyProductForm = () => ({
  name: '',
  description: '',
  price: '',
  originalPrice: '',
  mainImage: null,
  additionalImages: [],
  category: '',
  categoryId: '',
  features: '',
  stockQuantity: '',
  sku: '',
  weight: '',
  dimensions: '',
  tags: '',
  brand: '',
  material: '',
  closure: '',
  color: '',
  colorCode: '',
  colors: '',
  variants: [],
  imageColorMaps: [],
  fastShipping: false,
  rating: '',
  reviews: '',
  badge: '',
  isActive: true,
  isFeatured: false,
});

const serializeColorRows = (rows) => rows
  .filter((r) => r.name.trim() && r.code.trim())
  .map((r) => `${r.name.trim()}: ${r.code.trim()}`)
  .join(', ');

const parseColorsToRows = (colorsJson) => {
  if (!colorsJson) return [emptyColorRow()];
  try {
    const parsed = JSON.parse(colorsJson);
    if (Array.isArray(parsed) && parsed.length > 0) {
      return parsed.map((c) => ({ name: c.name || '', code: c.code || '#000000' }));
    }
  } catch (e) { /* fall through to default row */ }
  return [emptyColorRow()];
};

// Safely reads a product's `images` field, which the API may hand back as
// either a JSON string (raw DB column) or an already-parsed array (some
// callers normalize first) — a bare JSON.parse(product.images) blows up on
// the array case and on null/''. Every place that needs this list (the add
// flow's photo-colors step, the Edit modal's "Current Images", the View
// modal) should go through this instead of re-implementing the parse.
export const parseProductImages = (product) => {
  if (!product || !product.images) return [];
  if (Array.isArray(product.images)) return product.images;
  try {
    const parsed = JSON.parse(product.images);
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
};

// Derives the {imageId, imageUrl} list a product actually has right now,
// pre-filled with any existing per-image color mapping for that image.
const getProductImageList = (product, existingMaps = []) => {
  if (!product) return [];
  const mapByUrl = new Map(existingMaps.map((m) => [m.imageUrl, m]));
  const list = [];
  if (product.image) {
    const existing = mapByUrl.get(product.image);
    list.push({ imageId: existing?.imageId || 'main', imageUrl: product.image, label: 'Main image', colorName: existing?.colorName || '', colorCode: existing?.colorCode || '#000000' });
  }
  parseProductImages(product).forEach((url, idx) => {
    const existing = mapByUrl.get(url);
    list.push({ imageId: existing?.imageId || `additional-${idx}`, imageUrl: url, label: `Photo ${idx + 1}`, colorName: existing?.colorName || '', colorCode: existing?.colorCode || '#000000' });
  });
  return list;
};

/**
 * Owns everything the Inventory tab needs: product/category data, the
 * Add/Edit product forms (including the structured colors + per-image colors
 * inputs), and the CRUD handlers. `activeTab` gates the initial product fetch;
 * `showNotification` is the dashboard-wide toast function.
 */
export default function useInventory({ activeTab, showNotification }) {
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [categories, setCategories] = useState([]);
  const [productFilter, setProductFilter] = useState('all');
  const [productPage, setProductPage] = useState(1);

  const [showAddProduct, setShowAddProduct] = useState(false);
  // Set once a new product has been created, while the Add modal stays open
  // for the optional "assign photo colors" step (images only get real server
  // URLs after upload, so this can't happen before creation).
  const [justCreatedProduct, setJustCreatedProduct] = useState(null);
  const [showEditProduct, setShowEditProduct] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [showViewProduct, setShowViewProduct] = useState(false);
  const [viewingProduct, setViewingProduct] = useState(null);
  // URLs of the product's current (already-saved) images the admin removed
  // via the cancel icon in the "Current Images" section, to be sent to the
  // server on submit — the main image's URL may appear here too.
  const [removedImageUrls, setRemovedImageUrls] = useState([]);

  const [productForm, setProductForm] = useState(emptyProductForm());

  // Structured color rows for the Add/Edit product forms — the backend's
  // product.colors field is a "Name: #hex, Name: #hex" string (see
  // backend/src/routes/inventory.js), but having admins hand-type that is
  // exactly how existing product data ended up with typos like "#202E49z"
  // and stray commas. This UI state builds the string from real inputs instead.
  const [colorRows, setColorRows] = useState([emptyColorRow()]);
  const addColorRow = () => setColorRows((prev) => [...prev, emptyColorRow()]);
  const removeColorRow = (index) => setColorRows((prev) => prev.filter((_, i) => i !== index));
  const updateColorRow = (index, field, value) => setColorRows((prev) => prev.map((r, i) => (i === index ? { ...r, [field]: value } : r)));

  // Per-image color mapping — the higher-fidelity alternative to the plain
  // `colors` list above: lets each individual product photo carry its own
  // color, so the storefront can show "this exact photo = this exact color"
  // instead of one shared swatch list for every image.
  const [imageColorRows, setImageColorRows] = useState([]);
  const updateImageColorRow = (imageId, field, value) => setImageColorRows((prev) => prev.map((r) => (r.imageId === imageId ? { ...r, [field]: value } : r)));
  const saveImageColorMappings = async (productId, rows) => {
    const mappings = rows.filter((r) => r.colorName.trim()).map((r) => ({ imageUrl: r.imageUrl, imageId: r.imageId, colorName: r.colorName.trim(), colorCode: r.colorCode }));
    if (mappings.length === 0) return;
    const token = localStorage.getItem('adminToken');
    await fetch(`${API_BASE_URL}/api/admin/image-colors/products/${productId}/image-colors/bulk`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ mappings }),
    });
  };

  const fetchProducts = async () => {
    setLoadingProducts(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/admin/inventory/products`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (response.ok) {
        setProducts(await response.json());
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoadingProducts(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/categories`);
      if (response.ok) {
        const data = await response.json();
        setCategories(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  useEffect(() => {
    if (activeTab === 'inventory') fetchProducts();
  }, [activeTab]);

  useEffect(() => {
    fetchCategories();
  }, []);

  const getFilteredProducts = () => {
    let filtered = products;
    if (productFilter === 'active') filtered = products.filter((p) => p.isActive);
    if (productFilter === 'inactive') filtered = products.filter((p) => !p.isActive);
    if (productFilter === 'low_stock') filtered = products.filter((p) => p.stockQuantity <= 5);
    return filtered;
  };

  const getPaginatedProducts = () => {
    const filtered = getFilteredProducts();
    const totalPages = Math.max(1, Math.ceil(filtered.length / PRODUCTS_PER_PAGE));
    const safePage = Math.min(productPage, totalPages);
    const startIndex = (safePage - 1) * PRODUCTS_PER_PAGE;
    return filtered.slice(startIndex, startIndex + PRODUCTS_PER_PAGE);
  };

  const viewLowStockDetails = () => setProductFilter('low_stock');

  const handleMainImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) setProductForm((prev) => ({ ...prev, mainImage: file }));
  };

  const handleAdditionalImagesUpload = (event) => {
    const files = Array.from(event.target.files);
    if (files.length > 0) {
      setProductForm((prev) => ({ ...prev, additionalImages: [...prev.additionalImages, ...files].slice(0, 10) }));
    }
  };

  const removeAdditionalImage = (index) => {
    setProductForm((prev) => ({ ...prev, additionalImages: prev.additionalImages.filter((_, i) => i !== index) }));
  };

  // Marks one of the product's existing (already-saved) images for removal —
  // used by the cancel icon in the Edit modal's "Current Images" section.
  const removeCurrentImage = (url) => {
    setRemovedImageUrls((prev) => [...prev, url]);
    setImageColorRows((prev) => prev.filter((r) => r.imageUrl !== url));
  };

  const openAddProduct = () => {
    setColorRows([emptyColorRow()]);
    setShowAddProduct(true);
  };

  const closeAddProduct = () => {
    setShowAddProduct(false);
    setColorRows([emptyColorRow()]);
  };

  const handleCreateProduct = async (e) => {
    e.preventDefault();
    setLoadingProducts(true);

    const requestStartTime = Date.now();
    try {
      const token = localStorage.getItem('adminToken');
      const formData = new FormData();
      formData.append('productData', JSON.stringify({ ...productForm, colors: serializeColorRows(colorRows) }));

      if (productForm.mainImage) formData.append('mainImage', productForm.mainImage);
      productForm.additionalImages.forEach((image) => formData.append('additionalImages', image));

      const response = await fetch(`${API_BASE_URL}/api/admin/inventory/products`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });

      if (response.ok) {
        const data = await response.json();

        setProductForm(emptyProductForm());
        setColorRows([emptyColorRow()]);
        await fetchProducts();
        showNotification('Product created successfully!', 'success');

        // Images now have real server URLs (data.image/data.images) — offer
        // the optional per-photo color step instead of closing immediately.
        const images = getProductImageList(data, []);
        if (images.length > 0) {
          setImageColorRows(images);
          setJustCreatedProduct(data);
        } else {
          setShowAddProduct(false);
        }
      } else {
        const contentType = response.headers.get('content-type');
        let errorMessage = 'Failed to create product';
        try {
          if (contentType && contentType.includes('application/json')) {
            const error = await response.json();
            errorMessage = error.error || errorMessage;
          } else {
            const textError = await response.text();
            errorMessage = `Server error (${response.status}): ${textError.substring(0, 100)}`;
          }
        } catch (parseError) {
          errorMessage = `Server error (${response.status}): Could not parse response`;
        }
        showNotification(errorMessage, 'error');
      }
    } catch (error) {
      const elapsedTime = Date.now() - requestStartTime;
      console.error('[Product Creation] Error:', error);
      showNotification(`Network error (${elapsedTime}ms). Please try again.`, 'error');
    } finally {
      setLoadingProducts(false);
    }
  };

  // Closes the post-creation "assign photo colors" step, with or without saving.
  const finishAddProductFlow = async (save) => {
    if (save && justCreatedProduct) {
      try {
        await saveImageColorMappings(justCreatedProduct.id, imageColorRows);
      } catch (error) {
        console.warn('Error saving image color mappings:', error);
      }
    }
    setJustCreatedProduct(null);
    setImageColorRows([]);
    setShowAddProduct(false);
    if (save) await fetchProducts();
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setProductForm({
      name: product.name,
      description: product.description || '',
      price: product.price.toString(),
      originalPrice: product.originalPrice ? product.originalPrice.toString() : '',
      mainImage: null,
      additionalImages: [],
      category: product.category,
      categoryId: product.categoryId || '',
      features: product.features ? JSON.parse(product.features).join(', ') : '',
      stockQuantity: product.stockQuantity.toString(),
      sku: product.sku || '',
      weight: product.weight ? product.weight.toString() : '',
      dimensions: product.dimensions || '',
      tags: product.tags ? JSON.parse(product.tags).join(', ') : '',
      brand: product.brand || '',
      material: product.material || '',
      closure: product.closure || '',
      color: product.color || '',
      colorCode: product.colorCode || '',
      colors: product.colors ? JSON.parse(product.colors).map((c) => `${c.name}: ${c.code}`).join(', ') : '',
      variants: product.variants ? JSON.parse(product.variants) : [],
      imageColorMaps: [],
      fastShipping: product.fastShipping,
      rating: product.rating ? product.rating.toString() : '',
      reviews: product.reviews ? product.reviews.toString() : '',
      badge: product.badge || '',
      isActive: product.isActive,
      isFeatured: product.isFeatured,
    });
    setColorRows(parseColorsToRows(product.colors));
    setImageColorRows(getProductImageList(product, product.imageColorMaps || []));
    setRemovedImageUrls([]);
    setShowEditProduct(true);
  };

  const closeEditProduct = () => {
    setShowEditProduct(false);
    setEditingProduct(null);
    setRemovedImageUrls([]);
  };

  // Clicking a product opens this first — a read-only summary the admin can
  // act on (edit / activate / delete) instead of landing straight in the
  // Edit form with no overview.
  const openViewProduct = (product) => {
    setViewingProduct(product);
    setShowViewProduct(true);
  };

  const closeViewProduct = () => {
    setShowViewProduct(false);
    setViewingProduct(null);
  };

  const viewToEdit = () => {
    const product = viewingProduct;
    closeViewProduct();
    if (product) handleEditProduct(product);
  };

  const handleUpdateProduct = async (e) => {
    e.preventDefault();
    setLoadingProducts(true);

    try {
      const token = localStorage.getItem('adminToken');
      const formData = new FormData();
      const removeMainImage = removedImageUrls.includes(editingProduct.image);
      const removeImages = removedImageUrls.filter((url) => url !== editingProduct.image);
      formData.append('productData', JSON.stringify({ ...productForm, colors: serializeColorRows(colorRows), removeImages, removeMainImage }));

      if (productForm.mainImage) formData.append('mainImage', productForm.mainImage);
      productForm.additionalImages.forEach((image) => formData.append('additionalImages', image));

      const response = await fetch(`${API_BASE_URL}/api/admin/inventory/products/${editingProduct.id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });

      if (response.ok) {
        // Image-color rows reference the image URLs this product had when the
        // form opened — only safe to save if those images weren't just
        // replaced by this same submit. If new photos were uploaded, the
        // admin can assign their colors on the next edit once saved.
        if (!productForm.mainImage && productForm.additionalImages.length === 0) {
          try {
            await saveImageColorMappings(editingProduct.id, imageColorRows);
          } catch (mapError) {
            console.warn('Error saving image color mappings:', mapError);
          }
        }
        closeEditProduct();
        await fetchProducts();
        showNotification('Product updated successfully!', 'success');
      } else {
        const error = await response.json();
        showNotification(error.error || 'Failed to update product', 'error');
      }
    } catch (error) {
      console.error('Error updating product:', error);
      showNotification('Network error. Please try again.', 'error');
    } finally {
      setLoadingProducts(false);
    }
  };

  const handleToggleActive = async (productId, isActive) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/admin/inventory/products/${productId}/status`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ isActive: !isActive }),
      });
      if (response.ok) {
        await fetchProducts();
        showNotification(`Product ${!isActive ? 'activated' : 'deactivated'} successfully!`, 'success');
      } else {
        const error = await response.json();
        showNotification(error.error || 'Failed to update product status', 'error');
      }
    } catch (error) {
      console.error('Error toggling product status:', error);
      showNotification('Network error. Please try again.', 'error');
    }
  };

  const handleDeleteProduct = async (productId, productName) => {
    if (!window.confirm(`Are you sure you want to delete "${productName}"? This action cannot be undone.`)) return;
    setLoadingProducts(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/admin/inventory/products/${productId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (response.ok) {
        await fetchProducts();
        showNotification('Product deleted successfully!', 'success');
      } else {
        const error = await response.json();
        showNotification(error.error || 'Failed to delete product', 'error');
      }
    } catch (error) {
      console.error('Error deleting product:', error);
      showNotification('Network error. Please try again.', 'error');
    } finally {
      setLoadingProducts(false);
    }
  };

  const viewDelete = () => {
    const product = viewingProduct;
    if (!product) return;
    closeViewProduct();
    handleDeleteProduct(product.id, product.name);
  };

  const viewToggleActive = () => {
    const product = viewingProduct;
    if (!product) return;
    closeViewProduct();
    handleToggleActive(product.id, product.isActive);
  };

  return {
    // data
    products, categories, loadingProducts,
    productFilter, setProductFilter, productPage, setProductPage, PRODUCTS_PER_PAGE,
    getFilteredProducts, getPaginatedProducts, viewLowStockDetails,
    // view modal state
    showViewProduct, viewingProduct, openViewProduct, closeViewProduct,
    viewToEdit, viewDelete, viewToggleActive,
    // add/edit modal state
    showAddProduct, openAddProduct, closeAddProduct,
    justCreatedProduct, finishAddProductFlow,
    showEditProduct, editingProduct, closeEditProduct,
    productForm, setProductForm,
    handleMainImageUpload, handleAdditionalImagesUpload, removeAdditionalImage,
    removedImageUrls, removeCurrentImage,
    // colors (plain list)
    colorRows, addColorRow, removeColorRow, updateColorRow,
    // colors (per-image)
    imageColorRows, updateImageColorRow,
    // CRUD
    handleCreateProduct, handleEditProduct, handleUpdateProduct, handleToggleActive, handleDeleteProduct,
  };
}
